import React, { useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, Tab, Box, styled } from "@mui/material";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import TwoWheelerRoundedIcon from "@mui/icons-material/TwoWheelerRounded";
import WeekendRoundedIcon from "@mui/icons-material/WeekendRounded";
import WaterDropRoundedIcon from "@mui/icons-material/WaterDropRounded";
import GridOnRoundedIcon from "@mui/icons-material/GridOnRounded";
import {
  SERVICE_CATEGORY,
  SERVICE_CATEGORY_LABEL,
  SERVICE_CATEGORY_SLUG,
  getServiceCategoryFromPathname,
  toServiceCategory,
} from "utils/serviceCategory";

const BRAND = {
  primary: "#6C3CE9",
  primaryDark: "#5B2FD1",
  track: "#F4F3F8",
  textMuted: "#6B6F80",
  textActive: "#241A44",
};

const TABS = [
  { label: SERVICE_CATEGORY_LABEL[SERVICE_CATEGORY.CAR], value: SERVICE_CATEGORY.CAR, Icon: DirectionsCarRoundedIcon },
  { label: SERVICE_CATEGORY_LABEL[SERVICE_CATEGORY.BIKE], value: SERVICE_CATEGORY.BIKE, Icon: TwoWheelerRoundedIcon },
  { label: SERVICE_CATEGORY_LABEL[SERVICE_CATEGORY.SOFA], value: SERVICE_CATEGORY.SOFA, Icon: WeekendRoundedIcon },
  { label: SERVICE_CATEGORY_LABEL[SERVICE_CATEGORY.TANK], value: SERVICE_CATEGORY.TANK, Icon: WaterDropRoundedIcon },
  { label: SERVICE_CATEGORY_LABEL[SERVICE_CATEGORY.CARPET], value: SERVICE_CATEGORY.CARPET, Icon: GridOnRoundedIcon },
] as const;

const StyledTabs = styled(Tabs)(() => ({
  minHeight: 52,
  padding: 6,
  backgroundColor: BRAND.track,
  borderRadius: 16,
  border: "1px solid #EAE8F2",
  boxShadow: "inset 0 1px 2px rgba(20, 10, 60, 0.03)",
  "& .MuiTabs-indicator": {
    height: "100%",
    borderRadius: 12,
    background: `linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.primaryDark} 100%)`,
    boxShadow: "0 6px 16px -4px rgba(108, 60, 233, 0.55)",
    zIndex: 0,
  },
  "& .MuiTabs-flexContainer": {
    position: "relative",
    zIndex: 1,
    gap: 4,
  },
}));

const StyledTab = styled(Tab)(() => ({
  minHeight: 40,
  height: 40,
  minWidth: "unset",
  padding: "0 20px",
  borderRadius: 12,
  textTransform: "none",
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: 0.15,
  flexDirection: "row",
  gap: 8,
  color: BRAND.textMuted,
  transition: "color 0.25s ease, transform 0.15s ease",
  "& svg": {
    width: 17,
    height: 17,
    transition: "transform 0.25s ease",
  },
  "&:hover": {
    color: BRAND.textActive,
  },
  "&:hover svg": {
    transform: "scale(1.08)",
  },
  "&.Mui-selected": {
    color: "#FFFFFF",
    fontWeight: 700,
  },
  "& .MuiTab-iconWrapper": {
    marginRight: 0,
    marginBottom: 0,
  },
}));

interface CategoryTabsProps {
  /** Base path the tabs live under, e.g. "/service-management" */
  basePath: string;
  onChange: (category: SERVICE_CATEGORY) => void;
}

const CategoryTabs: React.FC<CategoryTabsProps> = ({ basePath, onChange }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeCategory = useMemo(
    () => getServiceCategoryFromPathname(location.pathname),
    [location.pathname]
  );

  React.useEffect(() => {
        onChange(activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const nextCategory = toServiceCategory(newValue);
    const nextPath = `${basePath}/${SERVICE_CATEGORY_SLUG[nextCategory]}`;
        navigate(nextPath);
  };

  return (
    <Box sx={{ display: "inline-flex", maxWidth: "100%" }}>
      <StyledTabs
        value={activeCategory}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons={false}
        aria-label="Service category tabs"
      >
        {TABS.map(({ label, value: tabValue, Icon }) => (
          <StyledTab
            key={tabValue}
            value={tabValue}
            disableRipple
            icon={<Icon />}
            iconPosition="start"
            label={label}
          />
        ))}
      </StyledTabs>
    </Box>
  );
};

export default CategoryTabs;
